import { motion } from "framer-motion";
import { Calculator, ArrowUpRight, Sparkles } from "lucide-react";
import { trackConversion } from "@/lib/consent";

/**
 * CalculatorCTA. Deep-Link zur cre8-studio-rechner.de.
 * Steht direkt nach den 6 Stufen und lädt Interessenten ein, sich sofort
 * ein Paket selbst zusammenzustellen und einen fairen Preis zu bekommen.
 */
export default function CalculatorCTA() {
  const handleClick = () => {
    // Google-Ads-Konversion "Klick auf externen Link". feuert nur nach Consent
    trackConversion("AW-18361166788/I4dWCJ695tocEMTXpLNE");
  };

  return (
    <section
      id="rechner"
      data-testid="calculator-cta-section"
      aria-labelledby="calculator-cta-heading"
      className="relative bg-ink py-16 lg:py-24"
    >
      {/* Ambient cyan bloom behind the card */}
      <div
        aria-hidden
        className="pointer-events-none absolute left-1/2 top-1/2 h-[500px] w-[900px] -translate-x-1/2 -translate-y-1/2"
        style={{
          background:
            "radial-gradient(closest-side, rgba(63,211,219,0.12) 0%, rgba(63,211,219,0.04) 45%, rgba(63,211,219,0) 75%)",
          filter: "blur(24px)",
        }}
      />

      <div className="relative mx-auto max-w-[1600px] px-6 lg:px-12">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-15%" }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="relative overflow-hidden rounded-2xl border border-signal/25 bg-gradient-to-br from-[#25262C] via-[#1E1F24] to-[#22232A] p-8 sm:p-12 lg:p-16"
        >
          {/* subtle grid overlay */}
          <div
            aria-hidden
            className="pointer-events-none absolute inset-0 opacity-[0.05]"
            style={{
              backgroundImage:
                "linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)",
              backgroundSize: "64px 64px",
            }}
          />

          <div className="relative grid gap-10 lg:grid-cols-[1fr_auto] lg:items-center lg:gap-14">
            <div>
              <div className="flex items-center gap-3 text-xs uppercase tracking-[0.25em] text-signal mb-5">
                <span className="h-px w-10 bg-signal" />
                <Sparkles size={14} />
                Kalkulationsrechner
              </div>

              <h2
                id="calculator-cta-heading"
                className="font-display font-black tracking-tight text-bone leading-[1.05] mb-5"
                style={{ fontSize: "clamp(1.75rem, 3.6vw, 3rem)" }}
              >
                Dein Paket in <span className="text-signal">60 Sekunden</span> konfigurieren.
              </h2>

              <p className="text-base lg:text-lg text-bone/70 leading-relaxed max-w-2xl mb-8">
                Dienstleistungen wählen, Laufzeit setzen, transparenten Preis
                erhalten. Ohne Rätselraten, ohne versteckte Kosten. Alle Preise
                netto zzgl. MwSt.
              </p>

              <ul className="flex flex-wrap gap-x-6 gap-y-3 text-sm text-bone/80 mb-8">
                {[
                  "Liveberechnung",
                  "Sofort als PDF",
                  "Laufzeitrabatt bis 25 %",
                ].map((f) => (
                  <li key={f} className="flex items-center gap-2">
                    <span className="inline-block w-1.5 h-1.5 rounded-full bg-signal" />
                    {f}
                  </li>
                ))}
              </ul>

              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                <a
                  href="https://www.cre8-studio-rechner.de"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={handleClick}
                  data-testid="calculator-cta-primary"
                  aria-label="Kalkulationsrechner öffnen und Paket konfigurieren"
                  className="btn-cyan-ghost inline-flex items-center gap-3 px-7 py-4 text-sm font-semibold rounded-full"
                >
                  <span className="inline-flex items-center justify-center w-7 h-7 rounded-full border border-signal/70 text-signal">
                    <Calculator size={14} />
                  </span>
                  Jetzt Paket konfigurieren
                  <ArrowUpRight size={14} className="opacity-70" />
                </a>
                <a
                  href="#contact"
                  data-testid="calculator-cta-secondary"
                  className="inline-flex items-center gap-2 text-sm text-bone/60 hover:text-signal transition-colors font-medium"
                >
                  oder direkt Kontakt aufnehmen
                  <ArrowUpRight size={12} />
                </a>
              </div>
            </div>

            {/* Right-side big calculator icon panel, only on desktop */}
            <div className="hidden lg:flex items-center justify-center relative">
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                whileInView={{ scale: 1, opacity: 1 }}
                viewport={{ once: true, margin: "-20%" }}
                transition={{ duration: 1, ease: [0.22, 1, 0.36, 1], delay: 0.3 }}
                className="relative w-56 h-56 rounded-2xl border border-signal/30 bg-white/40 backdrop-blur-sm flex items-center justify-center"
                style={{
                  boxShadow:
                    "0 20px 60px -20px rgba(63,211,219,0.35), inset 0 1px 0 rgba(255,255,255,0.05)",
                }}
              >
                <Calculator size={80} strokeWidth={1.1} className="text-signal" />
                <motion.div
                  aria-hidden
                  className="absolute -top-3 -right-3 h-12 w-12 rounded-full bg-signal text-bone flex items-center justify-center font-display font-black text-lg"
                  animate={{ rotate: [0, 6, -6, 0] }}
                  transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
                >
                  €
                </motion.div>
              </motion.div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
